import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  Eye, 
  Check, 
  X, 
  Search, 
  Filter, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Calendar,
  Building2,
  Phone,
  User,
  Star,
  Layers,
  MoreHorizontal
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { message, Modal, Dropdown } from 'antd';
import VietnameseDatePicker from '../../components/documents/VietnameseDatePicker';
import CustomSelect from '../../components/base/CustomSelect';
import {
  announceReceptionFeedback,
  readActiveReceptionFeedback,
  readRatedReceptionIds,
  RECEPTION_FEEDBACK_SESSION_EVENT,
  RECEPTION_FEEDBACK_UPDATED_EVENT,
} from '../../citizen/data/receptionFeedbackSession';

export const RECEPTION_DEPARTMENTS = [
  { value: '', label: 'Tất cả quầy' },
  { value: 'QUAY_1', label: 'Quầy 1' },
  { value: 'QUAY_2', label: 'Quầy 2' },
  { value: 'QUAY_3', label: 'Quầy 3' },
  { value: 'QUAY_4', label: 'Quầy 4' },
  { value: 'QUAY_5', label: 'Quầy 5' },
  { value: 'QUAY_6', label: 'Quầy 6' },
  { value: 'QUAY_7', label: 'Quầy 7' },
  { value: 'QUAY_8', label: 'Quầy 8' },
];

export default function ReceptionFeedbackDispatchPage() {
  const [activeReception, setActiveReception] = useState(() => readActiveReceptionFeedback());
  const [selectedReception, setSelectedReception] = useState(null);
  const [approvedReceptionIds, setApprovedReceptionIds] = useState(() => new Set());
  const [ratedReceptionIds, setRatedReceptionIds] = useState(() => readRatedReceptionIds());

  // Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  // Reject Modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Tickets state from API
  const [dbTickets, setDbTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Listen to local session updates
  useEffect(() => {
    const refresh = () => setActiveReception(readActiveReceptionFeedback());
    const refreshRatings = () => setRatedReceptionIds(readRatedReceptionIds());
    window.addEventListener(RECEPTION_FEEDBACK_SESSION_EVENT, refresh);
    window.addEventListener(RECEPTION_FEEDBACK_UPDATED_EVENT, refreshRatings);
    window.addEventListener('storage', refresh);
    window.addEventListener('storage', refreshRatings);
    return () => {
      window.removeEventListener(RECEPTION_FEEDBACK_SESSION_EVENT, refresh);
      window.removeEventListener(RECEPTION_FEEDBACK_UPDATED_EVENT, refreshRatings);
      window.removeEventListener('storage', refresh);
      window.removeEventListener('storage', refreshRatings);
    };
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/reception-registrations?size=100');
      const tickets = res?.data?.data || res?.data || [];
      if (Array.isArray(tickets)) {
        setDbTickets(tickets);
      } else {
        setDbTickets([]);
      }
    } catch (error) {
      console.error("Failed to fetch reception tickets", error);
      setDbTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleOpenDetail = async (reception) => {
    setSelectedReception(reception);
    const targetId = reception.rawId || reception.id || reception.receptionId;
    if (!targetId) return;

    try {
      setDetailLoading(true);
      const res = await apiClient.get(`/api/reception-registrations/${targetId}`);
      const d = res.data?.data || res.data;
      if (d) {
        const app = d.applicant || d.citizen || d.citizenInfo || {};
        const p = app.phoneNumber || app.phone || d.phone || d.phoneNumber || d.citizenPhone || reception.phone;
        const c = app.citizenId || app.identityCardNumber || app.cccd || d.cccd || d.citizenId || d.identityCardNumber || reception.cccd;
        const a = app.address || d.address || d.citizenAddress || reception.address;
        const n = app.fullName || app.name || d.citizenName || d.applicantName || d.fullName || reception.citizenName;
        const t = d.reason || d.topic || d.workingContent || d.content || d.title || reception.topic;
        const desc = d.workingContent || d.reason || d.description || d.content || reception.description || t;

        setSelectedReception(prev => ({
          ...prev,
          ...d,
          ticketNo: d.receptionCode || d.registrationCode || reception.ticketNo,
          receptionId: d.id || reception.receptionId,
          rawId: d.id || reception.rawId,
          topic: t,
          description: desc,
          date: d.receptionDate ? new Date(d.receptionDate).toLocaleDateString('vi-VN') : (reception.date || reception.receptionDate),
          slot: d.timeSlot || reception.slot,
          fullName: n,
          citizenName: n,
          phone: p && p !== '---' ? p : reception.phone,
          cccd: c && c !== '---' ? c : reception.cccd,
          address: a && a !== '---' ? a : reception.address,
          department: d.department || reception.department || 'QUAY_1',
          office: d.department || d.location || reception.office || 'Phòng tiếp công dân',
          approvalStatus: d.approvalStatus || d.status || reception.status,
          status: d.approvalStatus || d.status || reception.status,
        }));
      }
    } catch (err) {
      console.warn("Could not fetch remote detail, using row data", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const invite = (reception) => {
    setActiveReception(announceReceptionFeedback(reception));
    message.success('Đã gửi thông báo mời đánh giá đến thiết bị tiếp dân!');
  };

  const approveReception = async (reception) => {
    const targetId = reception.rawId || reception.id || reception.receptionId;
    const targetDept = reception.department || 'QUAY_1';
    try {
      await apiClient.patch(`/api/reception-registrations/${targetId}/approve`, {
        department: targetDept
      });
      message.success('Đã phê duyệt tiếp nhận thành công!');
      fetchTickets();
      setApprovedReceptionIds((current) => new Set([...current, targetId]));
    } catch (error) {
      console.error("Failed to approve ticket", error);
      message.error(error.response?.data?.message || 'Lỗi khi phê duyệt tiếp nhận');
    }
  };

  const completeReception = async (receptionId, rawId) => {
    const targetId = rawId || receptionId;
    try {
      await apiClient.patch(`/api/reception-registrations/${targetId}/complete`);
      message.success('Đã hoàn thành buổi tiếp dân!');
      fetchTickets();
    } catch (error) {
      console.error("Failed to complete ticket", error);
      message.error(error.response?.data?.message || 'Lỗi khi hoàn thành buổi tiếp dân');
    }
  };

  const handleOpenRejectModal = (reception) => {
    setRejectTarget(reception);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) {
      message.warning('Vui lòng nhập lý do từ chối tối thiểu 5 ký tự');
      return;
    }
    const targetId = rejectTarget.rawId || rejectTarget.id || rejectTarget.receptionId;
    try {
      setRejectLoading(true);
      await apiClient.patch(`/api/reception-registrations/${targetId}/reject`, {
        reason: rejectReason.trim()
      });
      message.success('Đã từ chối đơn tiếp dân thành công');
      setRejectModalOpen(false);
      setRejectTarget(null);
      fetchTickets();
    } catch (error) {
      console.error("Failed to reject ticket", error);
      message.error(error.response?.data?.message || 'Lỗi khi từ chối đơn tiếp dân');
    } finally {
      setRejectLoading(false);
    }
  };

  const displayItems = useMemo(() => {
    const rawItems = dbTickets.map(item => {
      const applicantObj = item.applicant || item.citizen || item.citizenInfo || {};
      const phoneVal = applicantObj.phoneNumber || applicantObj.phone || item.phone || item.phoneNumber || item.citizenPhone || '';
      const cccdVal = applicantObj.citizenId || applicantObj.identityCardNumber || applicantObj.cccd || item.cccd || item.citizenId || item.identityCardNumber || '';
      const addressVal = applicantObj.address || item.address || item.citizenAddress || '';
      const nameVal = applicantObj.fullName || applicantObj.name || item.citizenName || item.applicantName || item.fullName || 'Công dân';
      const topicVal = item.topic || item.reason || item.workingContent || item.content || item.title || 'Tiếp nhận ý kiến';
      const descVal = item.workingContent || item.reason || item.description || item.content || topicVal;

      return {
        ...item,
        rawId: item.id,
        receptionId: item.id || item.receptionCode || item.registrationCode,
        ticketNo: item.receptionCode || item.registrationCode || item.ticketNo || item.id,
        citizenName: nameVal,
        fullName: nameVal,
        topic: topicVal,
        description: descVal,
        date: item.receptionDate ? new Date(item.receptionDate).toLocaleDateString('vi-VN') : (item.date || 'Hôm nay'),
        slot: item.timeSlot || item.slot || 'Theo ca trực',
        office: item.office || item.department || item.location || 'Quầy tiếp dân',
        phone: phoneVal || '---',
        cccd: cccdVal || '---',
        address: addressVal || '---',
        status: item.approvalStatus || item.status || 'PENDING'
      };
    });

    let processed = rawItems.map(item => {
      const isRejected = item.approvalStatus === "REJECTED" || item.status === "REJECTED";
      const isRated = ratedReceptionIds.has(item.receptionId) || ratedReceptionIds.has(item.ticketNo) || item.ratingStatus === "RATED" || item.status === "RATED";
      const isCompleted = !isRated && (item.approvalStatus === "COMPLETED" || item.status === "COMPLETED");
      const isApproved = !isRated && !isCompleted && (approvedReceptionIds.has(item.receptionId) || item.approvalStatus === "APPROVED" || item.status === "APPROVED");
      const isPending = !isRejected && !isRated && !isCompleted && !isApproved;

      const currentStatusKey = isRejected ? 'REJECTED' : isRated ? 'RATED' : isCompleted ? 'COMPLETED' : isApproved ? 'APPROVED' : 'PENDING';
      const rank = isPending ? 1 : isApproved ? 2 : isCompleted ? 3 : isRated ? 4 : 5;

      return {
        ...item,
        currentStatusKey,
        rank
      };
    });

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      processed = processed.filter(item => 
        (item.ticketNo && String(item.ticketNo).toLowerCase().includes(q)) ||
        (item.citizenName && item.citizenName.toLowerCase().includes(q)) ||
        (item.phone && String(item.phone).toLowerCase().includes(q)) ||
        (item.topic && item.topic.toLowerCase().includes(q))
      );
    }

    if (statusFilter) {
      processed = processed.filter(item => item.currentStatusKey === statusFilter);
    }

    if (departmentFilter) {
      processed = processed.filter(item => 
        item.department === departmentFilter || 
        item.office === departmentFilter || 
        (item.department && item.department.includes(departmentFilter)) ||
        (item.office && item.office.includes(departmentFilter))
      );
    }

    if (dateFilter) {
      processed = processed.filter(item => {
        const itemDate = item.receptionDate ? new Date(item.receptionDate).toISOString().slice(0, 10) : item.date;
        return itemDate === dateFilter || (item.date && item.date.includes(dateFilter));
      });
    }

    processed.sort((a, b) => {
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      const timeB = new Date(b.receptionDate || b.date || b.created_at || 0).getTime();
      const timeA = new Date(a.receptionDate || a.date || a.created_at || 0).getTime();
      return timeB - timeA;
    });

    return processed;
  }, [dbTickets, approvedReceptionIds, ratedReceptionIds, search, statusFilter, dateFilter, departmentFilter]);

  // Action Menu Items for 3-dots dropdown
  const getActionMenuItems = (item) => {
    const isSelectedActive = activeReception?.receptionId === item.receptionId;
    const items = [
      {
        key: 'detail',
        label: (
          <span className="flex items-center gap-2 text-xs py-1 text-gray-700">
            <Eye className="w-4 h-4 text-blue-600" />
            <span>Xem chi tiết phiếu</span>
          </span>
        ),
        onClick: () => handleOpenDetail(item),
      },
    ];

    if (item.currentStatusKey === 'PENDING') {
      items.push(
        {
          key: 'approve',
          label: (
            <span className="flex items-center gap-2 text-xs py-1 text-emerald-600 font-medium">
              <Check className="w-4 h-4" />
              <span>Phê duyệt & Phân quầy</span>
            </span>
          ),
          onClick: () => approveReception(item),
        },
        {
          key: 'reject',
          label: (
            <span className="flex items-center gap-2 text-xs py-1 text-red-600 font-medium">
              <X className="w-4 h-4" />
              <span>Từ chối tiếp nhận</span>
            </span>
          ),
          onClick: () => handleOpenRejectModal(item),
        }
      );
    }

    if (item.currentStatusKey === 'APPROVED') {
      items.push({
        key: 'complete',
        label: (
          <span className="flex items-center gap-2 text-xs py-1 text-blue-600 font-medium">
            <CheckCircle2 className="w-4 h-4" />
            <span>Xác nhận tiếp xong</span>
          </span>
        ),
        onClick: () => completeReception(item.receptionId, item.rawId),
      });
    }

    return items;
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, dateFilter, departmentFilter]);

  const totalPages = Math.max(1, Math.ceil(displayItems.length / pageSize));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return displayItems.slice(start, start + pageSize);
  }, [displayItems, currentPage, pageSize]);

  return (
    <div className="min-h-screen">
      {/* Page Header Synchronized with UBND Design */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
            Quản lý lịch tiếp công dân
          </h1>
          <p className="text-sm text-gray-600">
            Kiểm tra thông tin đơn, phê duyệt lượt tiếp dân và quản lý đánh giá hài lòng tại các quầy
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={fetchTickets}
            disabled={loading}
            className="px-3.5 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 flex items-center gap-2 shadow-xs transition-colors"
          >
            <RotateCcw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* Filter Section Synchronized */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Filter className="w-4 h-4 text-blue-600" />
            <span>Bộ lọc tìm kiếm</span>
          </div>

          {(search || statusFilter || dateFilter || departmentFilter) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter(''); setDateFilter(''); setDepartmentFilter(''); }}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Đặt lại bộ lọc
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm kiếm theo mã đơn, công dân, SĐT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Status */}
          <div>
            <CustomSelect
              value={statusFilter}
              onChange={(val) => setStatusFilter(typeof val === 'object' ? val?.target?.value : val)}
              placeholder="Tất cả trạng thái"
              options={[
                { value: "", label: "Tất cả trạng thái" },
                { value: "PENDING", label: "Chờ duyệt / Tiếp nhận" },
                { value: "APPROVED", label: "Đã phân quầy" },
                { value: "COMPLETED", label: "Đã tiếp xong" },
                { value: "RATED", label: "Đã đánh giá" },
                { value: "REJECTED", label: "Từ chối tiếp" }
              ]}
            />
          </div>

          {/* Department */}
          <div>
            <CustomSelect
              value={departmentFilter}
              onChange={(val) => setDepartmentFilter(typeof val === 'object' ? val?.target?.value : val)}
              options={RECEPTION_DEPARTMENTS}
            />
          </div>

          {/* Date */}
          <div>
            <VietnameseDatePicker
              value={dateFilter}
              onChange={(isoVal) => setDateFilter(isoVal)}
              placeholder="dd/MM/yyyy"
            />
          </div>
        </div>
      </div>

      {/* Main Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin inline-block w-7 h-7 border-3 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="mt-2 text-sm text-gray-500 font-medium">Đang tải danh sách tiếp công dân...</p>
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm font-medium">Không có dữ liệu tiếp công dân</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Mã đơn
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Thông tin công dân
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Nội dung tiếp dân
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Thời gian / Quầy
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '160px' }}>
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((item) => {
                  const isSelectedActive = activeReception?.receptionId === item.receptionId;
                  return (
                    <tr key={item.receptionId || item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm font-bold text-blue-600">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(item)}
                          className="hover:underline hover:text-blue-800 text-left font-bold text-blue-600 cursor-pointer transition-colors"
                          title="Bấm để xem chi tiết phiếu tiếp dân"
                        >
                          {item.ticketNo || item.receptionId}
                        </button>
                      </td>
                      <td className="px-5 py-3.5 text-sm">
                        <div className="font-semibold text-gray-900">{item.citizenName}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" />
                          <span>{item.phone}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-700 max-w-xs truncate" title={item.topic}>
                        {item.topic}
                      </td>
                      <td className="px-5 py-3.5 text-sm whitespace-nowrap">
                        <div className="font-medium text-gray-900">{item.slot}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {item.date} — <span className="font-medium text-blue-700">{item.office}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-center text-xs">
                        {item.currentStatusKey === 'APPROVED' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Đã phân quầy
                          </span>
                        )}
                        {item.currentStatusKey === 'COMPLETED' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Đã tiếp xong
                          </span>
                        )}
                        {item.currentStatusKey === 'RATED' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            <Star className="w-3.5 h-3.5 fill-purple-700" /> Đã đánh giá
                          </span>
                        )}
                        {item.currentStatusKey === 'REJECTED' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-red-50 text-red-700 border border-red-200">
                            <XCircle className="w-3.5 h-3.5" /> Từ chối
                          </span>
                        )}
                        {item.currentStatusKey === 'PENDING' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            <Clock className="w-3.5 h-3.5" /> Chờ duyệt
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-center text-sm">
                        <Dropdown
                          menu={{ items: getActionMenuItems(item) }}
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <button
                            type="button"
                            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors inline-flex items-center justify-center border border-gray-200 shadow-xs"
                            title="Thao tác"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Phân trang */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="text-xs text-gray-500">
              Hiển thị <strong>{(currentPage - 1) * pageSize + 1}</strong> - <strong>{Math.min(currentPage * pageSize, displayItems.length)}</strong> trên <strong>{displayItems.length}</strong> đơn
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 text-xs font-semibold text-gray-700">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Chi tiết đơn tiếp dân */}
      <Modal
        title={<div className="font-bold text-gray-900 text-base">Chi tiết phiếu tiếp công dân</div>}
        open={Boolean(selectedReception)}
        onCancel={() => setSelectedReception(null)}
        footer={[
          <button
            key="close"
            onClick={() => setSelectedReception(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Đóng
          </button>
        ]}
        width={700}
      >
        {detailLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
            <p className="text-xs text-gray-500 mt-2">Đang tải thông tin chi tiết...</p>
          </div>
        ) : selectedReception && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Mã phiếu</span>
                <span className="font-bold text-blue-600 text-sm">{selectedReception.ticketNo || selectedReception.receptionId}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Họ tên công dân</span>
                <span className="font-bold text-gray-900 text-sm">{selectedReception.citizenName}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Số điện thoại</span>
                <span className="font-medium text-gray-800 text-sm">{selectedReception.phone}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">CCCD / Định danh</span>
                <span className="font-medium text-gray-800 text-sm">{selectedReception.cccd}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Thời gian tiếp nhận</span>
                <span className="font-medium text-gray-800 text-sm">{selectedReception.slot} ({selectedReception.date})</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Quầy phụ trách</span>
                <span className="font-bold text-blue-700 text-sm">{selectedReception.office}</span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs text-gray-500 block mb-1">Nội dung tiếp dân</span>
              <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">
                {selectedReception.topic}
              </p>
              {selectedReception.description && selectedReception.description !== selectedReception.topic && (
                <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200 whitespace-pre-wrap">
                  {selectedReception.description}
                </p>
              )}
            </div>

            {selectedReception.address && selectedReception.address !== '---' && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1">Địa chỉ công dân</span>
                <p className="text-xs text-gray-700">{selectedReception.address}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Từ chối đơn */}
      <Modal
        title={<div className="font-bold text-red-600 text-base">Từ chối đơn tiếp dân</div>}
        open={rejectModalOpen}
        onOk={handleConfirmReject}
        confirmLoading={rejectLoading}
        onCancel={() => { setRejectModalOpen(false); setRejectTarget(null); }}
        okText="Xác nhận từ chối"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <div className="pt-2">
          <p className="text-xs text-gray-600 mb-2">
            Vui lòng nhập lý do từ chối tiếp nhận cho mã đơn: <strong>{rejectTarget?.ticketNo || rejectTarget?.receptionId}</strong>
          </p>
          <textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Nhập lý do từ chối (tối thiểu 5 ký tự)..."
            className="w-full rounded-lg border border-gray-300 p-2.5 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
      </Modal>
    </div>
  );
}
